import { Button, useTranslation } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type {
  DispatchAssignmentDto,
  DispatchCategoryDto,
  DispatchEmployeeDto,
  DispatchJobDto,
} from './contractsMirror.js';

/**
 * The full-field edit dialog (Plan 22-23). Unlike the legacy reschedule-only path, a dispatcher can
 * change EVERY field of an existing assignment: crew member, day, start/end time, job, category,
 * notes, and customer contact. Confirming calls `onConfirm` with the edited values; the parent wires
 * it to `useEditMutation` (PATCH /api/v1/dispatch/:id — the server re-checks tenant ownership of any
 * job/category, SEC-11, and keeps unsupplied fields).
 *
 * Same focus-trap contract as `ScheduleFallbackDialog` / `ConfirmDialog` (focus-trapped, Esc
 * cancels, returns focus on close, `aria-labelledby` its heading). Token-only — no hex.
 */

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

export interface EditPick {
  scheduledDate: string;
  startTime: string;
  endTime: string | null;
  employeeId: number;
  jobId: number | null;
  categoryId: number | null;
  notes: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
}

export function AssignmentEditDialog({
  open,
  assignment,
  days,
  employees,
  jobs,
  categories,
  error,
  pending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  assignment: DispatchAssignmentDto | null;
  days: string[];
  employees: DispatchEmployeeDto[];
  jobs: DispatchJobDto[];
  categories: DispatchCategoryDto[];
  /** Why the last save failed, shown in the dialog so the dispatcher can fix it and retry. */
  error?: string | null;
  pending?: boolean;
  onConfirm: (pick: EditPick) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const [scheduledDate, setScheduledDate] = useState('');
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [jobId, setJobId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Hydrate the form from the assignment each time the dialog opens for a card.
  useEffect(() => {
    if (!open || !assignment) return;
    setScheduledDate(assignment.scheduledDate);
    setEmployeeId(assignment.employeeId);
    setStartTime(assignment.startTime ?? '08:00');
    setEndTime(assignment.endTime ?? '');
    setJobId(assignment.jobId ?? '');
    setCategoryId(assignment.categoryId ?? '');
    setNotes(assignment.notes ?? '');
    setCustomerName(assignment.customerName ?? '');
    setCustomerPhone(assignment.customerPhone ?? '');
    setCustomerAddress(assignment.customerAddress ?? '');
  }, [open, assignment]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    const firstFocusable = node?.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open || !assignment) return null;

  const canConfirm = scheduledDate !== '' && employeeId !== '' && startTime !== '';

  const submit = () => {
    if (scheduledDate === '' || startTime === '' || employeeId === '') return;
    onConfirm({
      scheduledDate,
      startTime,
      endTime: endTime || null,
      employeeId,
      jobId: jobId === '' ? null : jobId,
      categoryId: categoryId === '' ? null : categoryId,
      notes: notes.trim() || null,
      customerName: customerName.trim() || null,
      customerPhone: customerPhone.trim() || null,
      customerAddress: customerAddress.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-md border border-border bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {t('dispatch:edit.heading')}
        </h2>
        <p className="mt-1 text-muted-foreground text-sm">{t('dispatch:edit.body')}</p>
        {error ? (
          <p
            role="alert"
            className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-foreground text-sm"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-4 grid gap-4">
          <label className="block text-foreground text-sm" htmlFor={`${headingId}-day`}>
            {t('dispatch:fallback.day')}
            <select
              id={`${headingId}-day`}
              className={INPUT}
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            >
              {(days.includes(scheduledDate) ? days : [scheduledDate, ...days]).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-emp`}>
            {t('dispatch:fallback.employee')}
            <select
              id={`${headingId}-emp`}
              className={INPUT}
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">{t('dispatch:fallback.selectEmployee')}</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-start`}>
              {t('dispatch:fallback.startTime')}
              <input
                id={`${headingId}-start`}
                type="time"
                className={INPUT}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </label>
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-end`}>
              {t('dispatch:fallback.endTime')}
              <input
                id={`${headingId}-end`}
                type="time"
                className={INPUT}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </label>
          </div>

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-job`}>
            {t('dispatch:edit.job')}
            <select
              id={`${headingId}-job`}
              className={INPUT}
              value={jobId}
              onChange={(e) => setJobId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">{t('dispatch:edit.noJob')}</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.jobName}
                </option>
              ))}
            </select>
          </label>

          {categories.length > 0 && (
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cat`}>
              {t('dispatch:fallback.category')}
              <select
                id={`${headingId}-cat`}
                className={INPUT}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">{t('dispatch:fallback.noCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-notes`}>
            {t('dispatch:edit.notes')}
            <textarea
              id={`${headingId}-notes`}
              className={`${INPUT} h-20 py-2`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-name`}>
            {t('dispatch:fallback.customerName')}
            <input
              id={`${headingId}-cust-name`}
              type="text"
              className={INPUT}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-phone`}>
              {t('dispatch:fallback.customerPhone')}
              <input
                id={`${headingId}-cust-phone`}
                type="tel"
                className={INPUT}
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </label>
            <label className="block text-foreground text-sm" htmlFor={`${headingId}-cust-addr`}>
              {t('dispatch:fallback.customerAddress')}
              <input
                id={`${headingId}-cust-addr`}
                type="text"
                className={INPUT}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t('dispatch:fallback.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canConfirm || pending}>
            {t('dispatch:edit.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
